/**
 * A merchant who is ALREADY signed in arrives from the Shopify App Store
 * install with a store parked for them. Until 2026-09-24 the page attached
 * the store to whichever account the browser happened to hold — a demo
 * store landed in the owner's Command Center workspace without a question
 * asked. Now the merchant is told which account this is and chooses:
 * connect here, or sign out and use another account (the parked store
 * survives for an hour, and the params survive the sign-out).
 */
import { useEffect, useState, type ReactElement } from 'react';
import { apiClient, setAuthToken } from '@/services/api';
import { integrationsApi } from '@/services/api/integrationApi';
import { t } from '../../i18n';
import { takeShopifyConnectIntent } from '../../lib/shopifyHandoff';

interface Props {
  shop: string;
  name: string | null;
  onConnected: () => void;
  onFailed: () => void;
}

export function ShopifyConnectExisting({ shop, name, onConnected, onFailed }: Props): ReactElement {
  const [email, setEmail] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient.get<{ data?: { user?: { email?: string } } }>('/auth/me')
      .then((res) => { if (!cancelled) setEmail(res.data?.user?.email ?? null); })
      .catch(() => { /* the question still stands without the address */ });
    return () => { cancelled = true; };
  }, []);

  const connect = async (): Promise<void> => {
    setBusy(true);
    try {
      await integrationsApi.shopifyFinalize(shop);
      onConnected();
    } catch {
      setBusy(false);
      onFailed();
    }
  };

  // Connect was pressed inside BuzzBit minutes ago: the merchant already
  // chose this account, so attach without asking (see shopifyHandoff.ts).
  useEffect(() => {
    if (takeShopifyConnectIntent()) void connect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on arrival
  }, []);

  const useDifferentAccount = (): void => {
    // Drop the session only; the URL (and its ?shopify=connected&shop=) stays,
    // so the ordinary signup form takes over for the new account.
    setAuthToken(null);
    window.location.reload();
  };

  return (
    <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg space-y-3">
      {email && <p className="text-sm text-emerald-900">{t('integrations.handoff.signedInAs', { email })}</p>}
      <p className="text-sm font-medium text-emerald-900">
        {t('integrations.handoff.connectHereQuestion', { store: name || shop })}
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => { void connect(); }}
          disabled={busy}
          className="px-4 py-2 rounded-lg bg-black text-white text-sm font-medium disabled:opacity-60"
        >
          {busy ? t('integrations.handoff.connecting') : t('integrations.handoff.connectHere')}
        </button>
        <button
          type="button"
          onClick={useDifferentAccount}
          disabled={busy}
          className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700"
        >
          {t('integrations.handoff.useDifferentAccount')}
        </button>
      </div>
    </div>
  );
}
